import {
	Box,
	createStyles,
	Popover,
	Skeleton,
	UnstyledButton,
} from '@mantine/core';
import { Icon, Text } from '@repo/foundations';
import { useMount } from 'ahooks';
import { isEmpty } from 'lib0/object';
import { observer } from 'mobx-react-lite';
import { useState } from 'react';
import { sideBarStore } from '../store';
import { workspaceSwitchStore } from './store';
import { WorkspaceLogo } from './WorkspaceLogo';
import { WorkspaceSelectorDropdown } from './WorkspaceSelectorDropdown';

const useStyles = createStyles((theme) => ({
	dropdown: {
		width: theme.other.space[80],
		padding: 0,
	},
	targetButton: {
		display: 'flex',
		flex: '0 1 auto',
		gap: theme.spacing['2xs'],
		alignItems: 'center',
		borderRadius: theme.other.borderRadius.sm,

		height: theme.other.space[7],
		maxWidth: '100%',
		minWidth: 0, // Allow button to shrink
		padding: `0 ${theme.other.space[1]}px`,

		backgroundColor: theme.other.getColor('fill/transparent/default'),
		':hover': {
			backgroundColor: theme.other.getColor('fill/transparent/hover'),
		},
		':active': {
			backgroundColor: theme.other.getColor('fill/transparent/selected'),
		},
	},
	targetButtonSkeleton: {
		height: theme.other.space[7],
		maxWidth: `calc(100% - ${theme.other.space[7]}px)`,
	},
}));

export const WorkspaceSelector = observer(() => {
	const { classes } = useStyles();
	const [opened, setOpened] = useState(false);

	useMount(() => {
		workspaceSwitchStore.load();
	});

	const disabled = !workspaceSwitchStore.account;

	const buttonMaxWidth = sideBarStore.collapsed ? 'unset' : '100%';

	if (!workspaceSwitchStore.activeWorkspace) {
		return (
			<Skeleton animate={false} className={classes.targetButtonSkeleton} />
		);
	}

	return (
		<Popover
			classNames={{
				dropdown: classes.dropdown,
			}}
			position="bottom-start"
			withArrow={false}
			offset={{ mainAxis: 2 }}
			transitionProps={{
				transition: 'fade',
			}}
			disabled={disabled}
			onClose={() => setOpened(false)}
			opened={opened}
		>
			<Popover.Target>
				<UnstyledButton
					className={classes.targetButton}
					data-testid="workspace-selector-target-button"
					onClick={() => setOpened(!opened)}
					maw={buttonMaxWidth}
				>
					<Box style={{ display: 'flex', flex: '0 0 auto' }}>
						<WorkspaceLogo
							logo={
								!isEmpty(workspaceSwitchStore.activeWorkspace?.logo || '')
									? (workspaceSwitchStore.activeWorkspace.logo as string)
									: '/images/logos/cfsecoda.webp'
							}
							size={20}
						/>
					</Box>
					<Box
						style={{
							display: 'flex',
							flex: '1',
							minWidth: 0,
						}}
					>
						<Text
							size="sm"
							weight="bold"
							truncate
							style={{ flex: '1', minWidth: 0 }}
						>
							{workspaceSwitchStore.activeWorkspace.name}
						</Text>
						<Icon name="chevronDown" color="icon/secondary/default" />
					</Box>
				</UnstyledButton>
			</Popover.Target>
			<Popover.Dropdown>
				<WorkspaceSelectorDropdown
					workspaces={workspaceSwitchStore.account?.workspaces || []}
					setOpened={setOpened}
				/>
			</Popover.Dropdown>
		</Popover>
	);
});
